import {
  FREEZE_MAX_DAYS,
  FREEZE_MIN_DAYS,
  endManyLine,
  formatDayKey,
  freezeManyLine,
  freezeReasonLabels,
  localDate,
  movePackageEnd,
  packageFreezeReasons,
  packageLastInstant,
  pauseLastDay,
  pauseLine,
  type FreezeScope,
  type PackageFreezeReason,
  type PackagePause,
} from "@teamna/shared";
import { useState } from "react";
import { Button, ChipRow, Field, InlineError, Notice, Panel, showToast } from "../../components/index.ts";
import { useEndManyPauses, useEndPackageFreeze, useFreezeMany, useFreezePackage } from "@teamna/web-client/hooks/use-owner.ts";
import { formatDate } from "@teamna/web-client/lib/format.ts";
import { Stepper } from "../setup/frame.tsx";

/**
 * Freeze a pack (epic M3 "Freeze a pack", migration 0071): the owner pauses a package for travel,
 * injury or pregnancy, and the package end date moves by the paused days. The views take plain
 * props, so Storybook draws them; `FreezePanel` and `EndPausePanel` connect them to the API.
 * Freeze all (D17): `FreezeManyPanel` and `EndManyPausesPanel` do the same for every pack of a
 * client or a family, or of one child.
 */

/** Quick lengths for the days. The stepper sets any other number. */
const DAY_PRESETS = [7, 14, 30, 60, 90] as const;

export type FreezeInput = { startsOn: string; days: number; reason: PackageFreezeReason };

/** A pack the form says the new end date of. */
export type FreezePreviewPack = { packageName: string | null; expiresAt: string };

export function FreezePanelView({
  name,
  packageName,
  expiresAt,
  packs,
  title,
  timezone,
  today,
  pending = false,
  error = null,
  onSubmit,
  onClose,
}: {
  /** The participant, for the title. */
  name: string;
  packageName: string | null;
  /** The package end date now, ISO. */
  expiresAt: string;
  /**
   * Freeze all (D17): every pack the pause takes, with the same days and reason. When there is more
   * than one, the form lists the new end date of each. Left out: the one pack above.
   */
  packs?: FreezePreviewPack[];
  /** Left out: "Freeze the package of <name>". */
  title?: string;
  timezone: string;
  /** Today in the business timezone, `yyyy-MM-dd`. */
  today: string;
  pending?: boolean;
  error?: unknown;
  onSubmit: (input: FreezeInput) => void;
  onClose: () => void;
}) {
  const [startsOn, setStartsOn] = useState(today);
  const [days, setDays] = useState(14);
  const [reason, setReason] = useState<PackageFreezeReason>("travel");
  const valid = startsOn >= today && days >= FREEZE_MIN_DAYS && days <= FREEZE_MAX_DAYS;
  const endsOn = valid ? pauseLastDay(startsOn, days) : null;
  const newEnd = (iso: string) => formatDate(packageLastInstant(movePackageEnd(new Date(iso), days)).toISOString(), timezone);
  const many = packs && packs.length > 1 ? packs : null;

  return (
    <Panel
      open
      onClose={onClose}
      title={title ?? `Freeze the package of ${name}`}
      actions={
        <>
          <Button loading={pending} disabled={!valid} onClick={() => onSubmit({ startsOn, days, reason })}>
            Freeze
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="First paused day"
          type="date"
          value={startsOn}
          min={today}
          error={startsOn < today ? "Pick today or a later day." : undefined}
          onChange={(event) => setStartsOn(event.target.value)}
        />
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[15px] font-semibold">Days</span>
            <Stepper value={days} onChange={setDays} min={FREEZE_MIN_DAYS} max={FREEZE_MAX_DAYS} label="Paused days" />
          </div>
          <ChipRow
            options={DAY_PRESETS.map((value) => ({ id: String(value), label: `${value} days` }))}
            selectedId={String(days)}
            onSelect={(id) => setDays(Number(id))}
            label="Quick lengths"
            wrap
          />
        </div>
        <ChipRow
          options={packageFreezeReasons.map((value) => ({ id: value, label: freezeReasonLabels[value] }))}
          selectedId={reason}
          onSelect={(id) => setReason(id as PackageFreezeReason)}
          label="Reason"
          wrap
        />
        {endsOn && many ? (
          <div className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
            <p>
              {many.length} packages are paused from {formatDayKey(startsOn)} to {formatDayKey(endsOn)}. They cannot pay for a session in the
              pause. Each package ends {days} {days === 1 ? "day" : "days"} later:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {many.map((pack, index) => (
                <li key={`${pack.packageName ?? "package"}-${index}`}>
                  <span dir="auto">{pack.packageName ?? "The package"}</span> now ends on {newEnd(pack.expiresAt)}.
                </li>
              ))}
            </ul>
          </div>
        ) : endsOn ? (
          <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
            {packs?.[0]?.packageName ?? packageName ?? "The package"} is paused from {formatDayKey(startsOn)} to {formatDayKey(endsOn)}. It
            cannot pay for a session in the pause. The package now ends on {newEnd(packs?.[0]?.expiresAt ?? expiresAt)}.
          </p>
        ) : null}
        {error ? <InlineError error={error} /> : null}
      </div>
    </Panel>
  );
}

/**
 * The pause on a package card: "Paused until 14 Nov 2026" and, for the owner, "End the pause" in
 * the same box.
 */
export function PackagePauseNotice({ pause, today, onEnd }: { pause: PackagePause; today: string; onEnd?: () => void }) {
  return (
    <Notice
      tone="info"
      title={pauseLine(pause, today)}
      action={
        onEnd ? (
          <Button variant="secondary" fullWidth onClick={onEnd}>
            End the pause
          </Button>
        ) : undefined
      }
    >
      Reason: {freezeReasonLabels[pause.reason].toLowerCase()}. The package cannot pay for a session in the pause.
    </Notice>
  );
}

export function FreezePanel({
  businessId,
  participantId,
  purchaseId,
  name,
  packageName,
  expiresAt,
  timezone,
  onClose,
}: {
  businessId: string;
  participantId: string;
  purchaseId: string;
  name: string;
  packageName: string | null;
  expiresAt: string;
  timezone: string;
  onClose: () => void;
}) {
  const freeze = useFreezePackage(businessId);
  return (
    <FreezePanelView
      name={name}
      packageName={packageName}
      expiresAt={expiresAt}
      timezone={timezone}
      today={localDate(new Date(), timezone)}
      pending={freeze.isPending}
      error={freeze.error}
      onClose={onClose}
      onSubmit={(input) =>
        freeze.mutate(
          { participantId, purchaseId, ...input },
          {
            onSuccess: () => {
              showToast(`Package of ${name} paused`);
              onClose();
            },
          },
        )
      }
    />
  );
}

export function EndPausePanel({
  businessId,
  participantId,
  pause,
  name,
  timezone,
  onClose,
}: {
  businessId: string;
  participantId: string;
  pause: PackagePause;
  name: string;
  timezone: string;
  onClose: () => void;
}) {
  const end = useEndPackageFreeze(businessId);
  const today = localDate(new Date(), timezone);
  const started = pause.startsOn <= today;
  return (
    <Panel
      open
      onClose={onClose}
      title="End the pause?"
      actions={
        <>
          <Button
            loading={end.isPending}
            onClick={() =>
              end.mutate(
                { participantId, freezeId: pause.freezeId },
                {
                  onSuccess: () => {
                    showToast(`Pause of ${name} ended`);
                    onClose();
                  },
                },
              )
            }
          >
            End the pause
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="text-[15px] text-ink-2">
        {started
          ? `The pause ends today. The package of ${name} can pay for sessions from today. Its end date moves back by the days that are not used.`
          : `The pause has not started, so no days are used. The package end date moves back to the date before the pause.`}
      </p>
      {end.error ? (
        <div className="mt-3">
          <InlineError error={end.error} />
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Freeze all (D17): the same form as Freeze on one pack, for every pack in the scope. `all` is every
 * pack of the client or the family; `participant` the packs bought for one child. Each pack goes
 * through the rule of Freeze on one pack on the server, and a pack that cannot take the pause is
 * named in the toast.
 */
export function FreezeManyPanel({
  businessId,
  participantId,
  scope,
  title,
  name,
  packs,
  timezone,
  onClose,
}: {
  businessId: string;
  /** The participant the request goes through. With `all`, any participant of the account. */
  participantId: string;
  scope: FreezeScope;
  title: string;
  /** Who is paused, for the words of the panel. */
  name: string;
  /** The packs the pause takes, for the new end dates in the form. */
  packs: FreezePreviewPack[];
  timezone: string;
  onClose: () => void;
}) {
  const freeze = useFreezeMany(businessId);
  return (
    <FreezePanelView
      name={name}
      packageName={packs[0]?.packageName ?? null}
      expiresAt={packs[0]?.expiresAt ?? new Date().toISOString()}
      packs={packs}
      title={title}
      timezone={timezone}
      today={localDate(new Date(), timezone)}
      pending={freeze.isPending}
      error={freeze.error}
      onClose={onClose}
      onSubmit={(input) =>
        freeze.mutate(
          { participantId, scope, ...input },
          {
            onSuccess: (result) => {
              showToast(freezeManyLine(result.frozen, result.skipped));
              onClose();
            },
          },
        )
      }
    />
  );
}

/** End all pauses (D17): every pause in force or to come of the packs in the scope ends today. */
export function EndManyPausesPanel({
  businessId,
  participantId,
  scope,
  title,
  onClose,
}: {
  businessId: string;
  participantId: string;
  scope: FreezeScope;
  title: string;
  onClose: () => void;
}) {
  const end = useEndManyPauses(businessId);
  return (
    <Panel
      open
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button
            loading={end.isPending}
            onClick={() =>
              end.mutate(
                { participantId, scope },
                {
                  onSuccess: (result) => {
                    showToast(endManyLine(result.ended));
                    onClose();
                  },
                },
              )
            }
          >
            End the pauses
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="text-[15px] text-ink-2">
        Each pause ends today. The packages can pay for sessions from today. Each end date moves back by the days that are not used. A pause
        that has not started uses no days.
      </p>
      {end.error ? (
        <div className="mt-3">
          <InlineError error={end.error} />
        </div>
      ) : null}
    </Panel>
  );
}
